import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Heart, Award, Users, Clock } from 'lucide-react';

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Heart,
      title: 'Made with Love',
      description: 'Every item is baked fresh daily with care and passion'
    },
    {
      icon: Award,
      title: 'Premium Quality',
      description: 'We use only the finest ingredients for our products'
    },
    {
      icon: Users,
      title: 'Family Owned',
      description: 'Serving our community with warmth and dedication'
    },
    {
      icon: Clock,
      title: 'Fresh Daily',
      description: 'Our bakery items are prepared fresh every morning'
    }
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-white dark:bg-gray-800" data-testid="about-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/xuziuqqi_IMG-20250422-WA0009.jpg"
                alt="MKD Bakery fresh muffins"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="about-image"
              />
            </div>
            
            {/* Experience badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute -bottom-6 -right-6 bg-bakery-primary text-white p-6 rounded-2xl shadow-xl hidden md:block"
            >
              <p className="font-heading text-4xl font-bold">5.0</p>
              <p className="text-sm opacity-90">Star Rating</p>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="font-accent text-2xl text-bakery-primary">Our Story</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2 mb-6">
              A Taste of <span className="text-bakery-primary">Tradition</span>
            </h2>
            
            <p className="text-bakery-text/80 dark:text-bakery-bg/80 text-lg leading-relaxed mb-6">
              Welcome to <strong>MKD Bakery</strong>, your trusted local bakery in Tegeta, 
              Bagamoyo Road, Dar es Salaam. Since opening in May 2025, we take pride in creating fresh, delicious, 
              and beautifully crafted baked goods that bring joy to every occasion.
            </p>
            
            <p className="text-bakery-text/80 dark:text-bakery-bg/80 text-lg leading-relaxed mb-8">
              From our signature cakes and pastries to freshly baked bread and savory bites, 
              every item is made with premium ingredients and a lot of love. Whether you're 
              celebrating a birthday, hosting an event, or simply craving something sweet, 
              MKD Bakery is here to make your moments special.
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="p-3 bg-bakery-accent/50 dark:bg-gray-700 rounded-xl">
                    <feature.icon size={24} className="text-bakery-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-bakery-text dark:text-bakery-bg">{feature.title}</h4>
                    <p className="text-sm text-bakery-text/70 dark:text-bakery-bg/70">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
